import keyBy from 'lodash/keyBy';

const actionTypePrefix = 'PLAN_INTEGRATIONS';
// TRANSACTION because this state update can be used for insert, update and delete equally
export const TRANSACTION_START = `${actionTypePrefix}_TRANSACTION_START`;
export const TRANSACTION_ERROR = `${actionTypePrefix}_TRANSACTION_ERROR`;
export const UPSERT_ONE_SUCCESS = `${actionTypePrefix}_UPSERT_ONE_SUCCESS`;
export const UPSERT_SEVERAL_SUCCESS = `${actionTypePrefix}_UPSERT_SEVERAL_SUCCESS`;
export const DELETE_ONE_SUCCESS = `${actionTypePrefix}_DELETE_ONE_SUCCESS`;
export const DELETE_SEVERAL_SUCCESS = `${actionTypePrefix}_DELETE_SEVERAL_SUCCESS`;

// see https://help.xmatters.com/xmapi/index.html#get-an-integration
// for 1 integration object shape
const initialState = {
  error: '', // error message, straight from xM API
  awaiting: false, // awaiting instead of fetching or loading, because this can also be used while waiting for a deletion to complete
  byId: {}, // all the fetched integrations by id for easy getByid selector
  allIds: [], // all the IDS of the fetched integrations for easy getAll selector
  fetchedAt: null, // timeStamp in milliseconds, makes it easy to manipulate with "new Date(fetchedAt)"
  mapToPlanId: {}, // lookup integrationIds by planIds
};

/*
  mapToPlanId: {
    planId1: [integrationId1, integrationId2]
  }
*/

const mapToPlanIdReducer = (state, action) => {
  switch (action.type) {
    case UPSERT_ONE_SUCCESS: {
      const planId = action.payload.plan.id;
      const integrationId = action.payload.id;
      if (state[planId]) {
        return {
          ...state,
          [planId]: [
            ...state[planId].filter(id => id !== integrationId),
            integrationId,
          ],
        };
      }
      return {
        ...state,
        [planId]: [integrationId],
      };
    }
    case UPSERT_SEVERAL_SUCCESS: {
      const newState = action.payload.data.reduce(
        (mapToPlanId, integration) => {
          const planId = integration.plan.id;
          const newMap = { ...mapToPlanId };
          if (newMap[planId]) {
            newMap[planId] = [
              ...newMap[planId].filter(id => id !== integration.id),
              integration.id,
            ];
          } else {
            newMap[planId] = [integration.id];
          }
          return newMap;
        },
        { ...state },
      );
      return newState;
    }
    case DELETE_ONE_SUCCESS: {
      const planId = action.payload.plan.id;
      if (state[planId]) {
        return {
          ...state,
          [planId]: state[planId].filter(id => id !== action.payload.id),
        };
      }
      return state;
    }
    case DELETE_SEVERAL_SUCCESS: {
      const newState = action.payload.data.reduce(
        (mapToPlanId, integration) => {
          const planId = integration.plan.id;
          const newMap = { ...mapToPlanId };
          if (newMap[planId]) {
            newMap[planId] = newMap[planId].filter(id => id !== integration.id);
          }
          return newMap;
        },
        { ...state },
      );
      return newState;
    }
    default:
      return state;
  }
};

const planIntegrationsReducer = (state = initialState, action) => {
  switch (action.type) {
    case TRANSACTION_START:
      return {
        ...state,
        awaiting: true,
      };
    case TRANSACTION_ERROR:
      return {
        ...state,
        error: action.payload,
        awaiting: false,
      };
    case UPSERT_ONE_SUCCESS: {
      const byId = {
        ...state.byId,
        [action.payload.id]: action.payload,
      };
      return {
        error: '',
        awaiting: false,
        byId,
        allIds: Object.keys(byId),
        fetchedAt: Date.now(),
        mapToPlanId: mapToPlanIdReducer(state.mapToPlanId, action),
      };
    }
    case UPSERT_SEVERAL_SUCCESS: {
      const byId = { ...state.byId, ...keyBy(action.payload.data, 'id') };
      return {
        error: '',
        awaiting: false,
        byId,
        allIds: Object.keys(byId),
        fetchedAt: Date.now(),
        mapToPlanId: mapToPlanIdReducer(state.mapToPlanId, action),
      };
    }
    case DELETE_ONE_SUCCESS: {
      const byId = { ...state.byId };
      delete byId[action.payload.id];
      return {
        ...state,
        error: '',
        awaiting: false,
        byId,
        allIds: Object.keys(byId),
        mapToPlanId: mapToPlanIdReducer(state.mapToPlanId, action),
      };
    }
    // xM API doesn't have a "batch delete" method, but if it did, we'd handle it with this:
    case DELETE_SEVERAL_SUCCESS: {
      const byId = { ...state.byId };
      action.payload.data.forEach(integration => {
        delete byId[integration.id];
      });
      return {
        ...state,
        error: '',
        awaiting: false,
        byId,
        allIds: Object.keys(byId),
        mapToPlanId: mapToPlanIdReducer(state.mapToPlanId, action),
      };
    }
    default:
      return state;
  }
};

export default planIntegrationsReducer;
